import React, { forwardRef, useImperativeHandle } from 'react';
import FormField from '../../../../components/common/FormField';
import { FluidData } from '../../../../models/CompletionModels';
import { useFormValidation, useFormFieldHandler, validateTabFields } from '../../../../utilities/completionsUtils';
import { inflowModelSelect } from './CompletionDropdownData';

interface CompletionsFluidTabProps {
  fluid: FluidData;
  setFluid: React.Dispatch<React.SetStateAction<FluidData>>;
  externalValidationErrors?: { [fieldName: string]: string };
  updateValidationError: (tabname: string, errors: { [fieldName: string]: string }) => void;
}

const CompletionsFluidTab = forwardRef<any, CompletionsFluidTabProps>(
  ({ fluid, setFluid, externalValidationErrors = {}, updateValidationError }, ref) => {
    const validation = useFormValidation({
      tabName: 'fluid',
    });
    const updateValidation = (updatedData: FluidData) => {
      const fieldErrors = validateTabFields('fluid', updatedData);
      validation.setErrors?.(fieldErrors);
      updateValidationError('fluid', fieldErrors);
    };
    const handleFieldChange = useFormFieldHandler(setFluid, validation, updateValidation);

    const displayErrors =
      Object.keys(externalValidationErrors).length > 0 ? externalValidationErrors : validation.errors;

    useImperativeHandle(ref, () => ({
      triggerValidation: (data: FluidData) => {
        validation.validateAllFields(data);
      },
    }));

    return (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Oil gravity'
            name='oilGravity'
            type='text'
            value={fluid.oilGravity}
            required
            onChange={(_, v) => handleFieldChange('oilGravity', v as string)}
            error={displayErrors.oilGravity}
          />
          <FormField
            label='Gas gravity'
            name='gasGravity'
            type='text'
            value={fluid.gasGravity}
            required
            onChange={(_, v) => handleFieldChange('gasGravity', v as string)}
            error={displayErrors.gasGravity}
          />
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Reservoir pressure'
            name='reservoirPressure'
            type='text'
            value={fluid.reservoirPressure}
            required
            onChange={(_, v) => handleFieldChange('reservoirPressure', v as string)}
            error={displayErrors.reservoirPressure}
          />
          <FormField
            label='Reservoir temperature'
            name='reservoirTemperature'
            type='text'
            value={fluid.reservoirTemperature}
            required
            onChange={(_, v) => handleFieldChange('reservoirTemperature', v as string)}
            error={displayErrors.reservoirTemperature}
          />
        </div>
        <div style={{ display: 'flex', gap: 16 }}>
          <FormField
            label='Inflow model'
            name='inflowModel'
            type='select'
            value={fluid.inflowModel}
            onChange={(_, v) => handleFieldChange('inflowModel', v as string)}
            options={inflowModelSelect}
            required
            error={displayErrors.inflowModel}
          />
          <div style={{ flex: 1 }} />
        </div>
      </div>
    );
  },
);

export default CompletionsFluidTab;
